// ═══════════════════════════════════════════════════════════════════════════
//  LIRIK KARAOKE — gaya mesin karaoke klasik (5 Okt 2026)
//
//  Baris aktif besar: teks putih penuh dulu, lalu TERISI biru (gradasi
//  cyan → biru) dari kiri ke kanan mengikuti waktu nyanyi. Baris berikutnya
//  di bawahnya, lebih kecil & redup. Hitung mundur ● ● ● sebelum baris
//  pertama dan sesudah jeda panjang.
//
//  ── KENAPA TIDAK MEMAKAI STATE REACT PER FRAME ────────────────────────────
//
//  TV box Android murah. Sapuan digerakkan requestAnimationFrame yang menulis
//  `transform` langsung ke dua elemen (lihat .lk-isi-luar/.lk-isi-dalam di
//  pemutar.css): kotak pembungkus overflow:hidden digeser ke kiri, isinya
//  digeser balik ke kanan dengan jarak yang sama → teks biru diam di tempat,
//  yang bergerak hanya tepi potongnya. Dua-duanya lapisan GPU (will-change),
//  jadi tiap frame TIDAK ada layout maupun repaint — cuma komposit.
//  React hanya menggambar ulang saat baris berganti (±tiap 3 detik).
//
//  Waktu per kata (engine lokal / Enhanced LRC) dipakai bila ada; kalau
//  hanya per baris (LRC biasa) sapuannya linear sepanjang durasi baris.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { BarisKaraoke } from '../../lib/lirik'

// ── 6 Okt: BARIS KEPANJANGAN DIPECAH ─────────────────────────────────────────
// Zaldy: "ada yang sampai panjang lewat layar". Baris yang lebih panjang dari
// MAKS_HURUF dipecah di batas kata jadi beberapa baris pendek, waktunya ikut
// dibagi: per kata kalau ada (engine lokal / Enhanced LRC), selain itu
// sebanding jumlah huruf. Sapuan tetap satu baris layar per potong.
const MAKS_HURUF = { besar: 34, sedang: 42 }

function pecahBaris(b: BarisKaraoke, maks: number): BarisKaraoke[] {
  const pakaiKata = !!b.kata?.length
  const kata = pakaiKata ? b.kata!.map(k => k.teks) : b.teks.split(/\s+/).filter(Boolean)
  const total = kata.join(' ').length
  if (total <= maks || kata.length < 2) return [b]
  const n = Math.ceil(total / maks)
  const target = total / n
  const out: BarisKaraoke[] = []
  const dur = Math.max(0.2, b.a - b.t)
  let i = 0, hurufSebelum = 0
  while (i < kata.length) {
    let j = i, pjg = 0
    const sisaPotong = n - out.length
    while (j < kata.length && (j === i || sisaPotong <= 1 || pjg + 1 + kata[j].length <= Math.max(target, maks * 0.6) * 1.05)) {
      pjg += (j === i ? 0 : 1) + kata[j].length
      j++
    }
    const teks = kata.slice(i, j).join(' ')
    if (pakaiKata) {
      const ks = b.kata!.slice(i, j)
      out.push({ t: ks[0].t0, a: ks[ks.length - 1].t1, teks, kata: ks })
    } else {
      const t0 = b.t + dur * hurufSebelum / total
      const t1 = b.t + dur * Math.min(total, hurufSebelum + pjg + 1) / total
      out.push({ t: t0, a: t1, teks })
    }
    hurufSebelum += pjg + 1
    i = j
  }
  return out
}

type Ukuran = { W: number; kata: { x: number; w: number }[] }

/** Jeda (dtk) dari baris sebelumnya yang memunculkan hitung mundur. */
const JEDA_PANJANG = 5

export default function LirikKaraoke({ baris: barisAsli, waktu, ukuran = 'besar', romanisasi }: {
  baris: BarisKaraoke[]
  /** Posisi lagu sekarang (detik) — sudah termasuk offset. Dipanggil tiap frame. */
  waktu: () => number
  ukuran?: 'besar' | 'sedang'
  /** 21 Okt 2026: bacaan latin per teks baris (lagu Korea/Jepang/Mandarin/…).
   *  Bila ada, slot bawah menampilkan bacaan baris AKTIF, bukan baris berikutnya. */
  romanisasi?: (teks: string[]) => Promise<Record<string, string>>
}) {
  const baris = useMemo(
    () => barisAsli.flatMap(b => pecahBaris(b, MAKS_HURUF[ukuran])),
    [barisAsli, ukuran])
  const [rom, setRom] = useState<Record<string, string>>({})
  useEffect(() => {
    setRom({})
    if (!romanisasi || !baris.length) return
    let hidup = true
    romanisasi(baris.map(b => b.teks)).then(m => { if (hidup) setRom(m) }).catch(() => {})
    return () => { hidup = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baris])
  const [cur, setCur] = useState(0)
  const curRef = useRef(0)
  const wadahRef = useRef<HTMLDivElement>(null)
  const barisRef = useRef<HTMLDivElement>(null)
  const bawahRef = useRef<HTMLSpanElement>(null)
  const luarRef = useRef<HTMLSpanElement>(null)
  const dalamRef = useRef<HTMLSpanElement>(null)
  const titikRef = useRef<HTMLDivElement>(null)
  const ukur = useRef<Ukuran>({ W: 0, kata: [] })
  const xTerakhir = useRef(-1)
  const titikTerakhir = useRef(-1)

  // Baris berganti → ukur lebar teks & posisi tiap kata SEKALI, kecilkan bila
  // tidak muat satu baris layar (scale = transform, tanpa layout ulang).
  // 21 Okt 2026 — Zaldy: "di TV yang ukurannya lebih kecil teks berjalannya
  // jadi overlap, harusnya auto". Dulu dikecilkan lewat transform scale: kotak
  // layout-nya tetap selebar teks asli → di layar sempit inline-block itu
  // meluber ke kanan (tidak lagi di tengah) dan menimpa kontrol/tepi layar;
  // scale juga dibatasi 0,5. Sekarang FONT-nya yang dikecilkan sampai muat
  // (layout ikut mengecil, tetap di tengah, tanpa batas bawah selain 16px),
  // baru posisi kata diukur. Baris bawah (redup) diperlakukan sama.
  useLayoutEffect(() => {
    const el = barisRef.current
    const wadah = wadahRef.current
    xTerakhir.current = -1
    if (!el || !wadah) { ukur.current = { W: 0, kata: [] }; return }
    el.style.transform = ''
    el.style.fontSize = ''
    const dasar = el.querySelector<HTMLElement>('.lk-dasar')
    if (!dasar) return
    const muat = wadah.clientWidth * 0.94
    let W = dasar.offsetWidth
    if (W > muat && W > 0) {
      const dasarPx = parseFloat(getComputedStyle(el).fontSize) || 40
      el.style.fontSize = `${Math.max(16, Math.floor(dasarPx * muat / W * 10) / 10)}px`
      W = dasar.offsetWidth
    }
    const kata = Array.from(dasar.querySelectorAll<HTMLElement>('.lk-k'))
      .map(s => ({ x: s.offsetLeft, w: s.offsetWidth }))
    ukur.current = { W, kata }
    gambar(0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur, baris])
  useLayoutEffect(() => {
    const el = bawahRef.current
    const wadah = wadahRef.current
    if (!el || !wadah) return
    el.style.fontSize = ''
    const muat = wadah.clientWidth * 0.92
    const W = el.offsetWidth
    if (W > muat && W > 0) {
      const dasarPx = parseFloat(getComputedStyle(el).fontSize) || 26
      el.style.fontSize = `${Math.max(14, Math.floor(dasarPx * muat / W * 10) / 10)}px`
    }
  }, [cur, baris])

  function gambar(x: number) {
    const { W } = ukur.current
    const xb = Math.round(Math.max(0, Math.min(W, x)) * 2) / 2
    if (xb === xTerakhir.current) return
    xTerakhir.current = xb
    if (luarRef.current) luarRef.current.style.transform = `translate3d(${xb - W}px,0,0)`
    if (dalamRef.current) dalamRef.current.style.transform = `translate3d(${W - xb}px,0,0)`
  }

  useEffect(() => {
    curRef.current = 0
    setCur(0)
    if (!baris.length) return
    let hidup = true
    let id = 0
    const n = baris.length
    const loop = () => {
      if (!hidup) return
      const t = waktu()
      // Baris "sekarang" = belum selesai (+0,35 dtk tahan), dan baris
      // berikutnya belum mulai.
      let i = 0
      // 21 Okt 2026: baris berikutnya tampil 0,25 dtk SEBELUM mulai dinyanyikan
      // (dulu 0,08) — ditambah animasi masuk, baris dulu terasa telat muncul.
      while (i < n && (t >= baris[i].a + 0.35 || (i + 1 < n && t >= baris[i + 1].t - 0.25))) i++
      if (i !== curRef.current) {
        curRef.current = i
        setCur(i)
      } else if (i < n) {
        gambar(posisiX(baris[i], t, ukur.current))
        // ● ● ● hitung mundur
        const mulai = baris[i].t
        const jeda = i === 0 ? mulai : mulai - baris[i - 1].a
        const sisa = mulai - t
        const nTitik = jeda >= JEDA_PANJANG && sisa > 0 && sisa <= 3.6 ? Math.min(3, Math.ceil(sisa - 0.6) || 1) : 0
        if (nTitik !== titikTerakhir.current && titikRef.current) {
          titikTerakhir.current = nTitik
          const anak = titikRef.current.children
          titikRef.current.style.opacity = nTitik > 0 ? '1' : '0'
          for (let k = 0; k < anak.length; k++) {
            (anak[k] as HTMLElement).style.opacity = k < nTitik ? '1' : '0.15'
          }
        }
      }
      id = requestAnimationFrame(loop)
    }
    id = requestAnimationFrame(loop)
    return () => { hidup = false; cancelAnimationFrame(id) }
  }, [baris, waktu])

  if (!baris.length) return null
  const atas = baris[cur]
  const bawah = baris[cur + 1]
  const kataAtas = atas ? (atas.kata?.length ? atas.kata.map(k => k.teks) : atas.teks.split(/\s+/)) : []
  const isiKata = (beri: boolean) => kataAtas.map((w, k) => (
    <span key={k}>{k > 0 ? ' ' : ''}<span className={beri ? 'lk-k' : undefined}>{w}</span></span>
  ))

  return (
    <div ref={wadahRef} className={`lk-wadah ${ukuran === 'sedang' ? 'lk-sedang' : ''}`}>
      <div ref={titikRef} className="lk-titik" style={{ opacity: 0 }} aria-hidden>
        <i /><i /><i />
      </div>
      <div className="lk-slot-atas">
        {atas && (
          <div key={`a${cur}`} className="lk-masuk">
            <div ref={barisRef} className="lk-baris">
              <span className="lk-dasar">{isiKata(true)}</span>
              <span ref={luarRef} className="lk-isi-luar" aria-hidden>
                <span ref={dalamRef} className="lk-isi-dalam">{isiKata(false)}</span>
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="lk-slot-bawah">
        {atas && rom[atas.teks]
          ? <div key={`r${cur}`} className="lk-masuk-bawah lk-rom"><span ref={bawahRef} className="lk-bawah-teks">{rom[atas.teks]}</span></div>
          : bawah && <div key={`b${cur + 1}`} className="lk-masuk-bawah lk-redup"><span ref={bawahRef} className="lk-bawah-teks">{bawah.teks}</span></div>}
      </div>
    </div>
  )
}

/** Posisi tepi sapuan (px dari kiri teks) untuk waktu `t`. */
function posisiX(b: BarisKaraoke, t: number, u: Ukuran): number {
  const { W, kata } = u
  if (W <= 0 || t <= b.t) return 0
  if (t >= b.a) return W
  if (b.kata && b.kata.length && b.kata.length === kata.length) {
    for (let j = 0; j < b.kata.length; j++) {
      const k = b.kata[j]
      if (t < k.t0) return j === 0 ? 0 : kata[j - 1].x + kata[j - 1].w
      if (t < k.t1) return kata[j].x + kata[j].w * (t - k.t0) / Math.max(0.05, k.t1 - k.t0)
    }
    return W
  }
  return W * (t - b.t) / Math.max(0.2, b.a - b.t)
}
